/**
 * Fleet Control Centre overview: five cards (Orders, Drivers, Compliance,
 * Maintenance, Telematics), today only. See ControlCentreCards. The System
 * Health cards and 7-day trends live on their own page (tabs/SystemHealth.tsx).
 */

import { Brain, Rocket } from 'lucide-react';
import type { AnalyticsOverview, DashboardOverview } from '../../types/fleetDataHub';
import ControlCentreCards from './ControlCentreCards';
import type { NavSection, TelematicsTotalsLike } from '../../utils/controlCentreCards';

export interface FleetHubOverviewProps {
  analyticsOverview: AnalyticsOverview | null;
  dashboardOverview: DashboardOverview | null;
  onFetchData: () => void;
  isLoading: boolean;
  onNavigate?: (section: NavSection, highlightKPI?: string) => void;
  useMockData?: boolean;
  /** Vehicle counts the hub already works out from the telematics providers. */
  telematicsTotals?: TelematicsTotalsLike | null;
  /** The server says this org gets Order Flow Intelligence (GET /orders/v1/availability). */
  ordersDailyEnabled?: boolean;
  /** The server's reason the board isn't available yet, when it gave one. */
  ordersUnavailableMessage?: string;
  /** The Control Centre is the page on screen (pages stay mounted but hidden). */
  active?: boolean;
}

export default function FleetHubOverview({
  analyticsOverview,
  dashboardOverview,
  onFetchData,
  isLoading,
  onNavigate,
  useMockData = false,
  telematicsTotals,
  ordersDailyEnabled = false,
  ordersUnavailableMessage,
  active = true,
}: FleetHubOverviewProps) {
  // If no data, show fetch prompt
  if (!analyticsOverview && !dashboardOverview) {
    return (
      <div className="text-center py-16">
        <div className="relative">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-32 h-32 bg-ring/10 rounded-full animate-pulse"></div>
          </div>
          <Brain className="w-20 h-20 text-ring mx-auto mb-6 relative z-10 animate-bounce" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-3">Fleet Intelligence Awaits</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          Power up your control centre with real-time analytics and operational insights
        </p>
        <button
          onClick={onFetchData}
          disabled={isLoading}
          className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-ring to-chart-5 hover:from-ring hover:to-chart-5 disabled:from-ring/50 disabled:to-chart-5/50 text-white rounded-xl transition-all shadow-lg hover:shadow-ring/50 transform hover:scale-105"
        >
          <Rocket className={`w-6 h-6 ${isLoading ? 'animate-spin' : ''}`} />
          <span className="font-semibold">{isLoading ? 'Initializing...' : 'Activate Control Centre'}</span>
        </button>
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* Page title lives in FleetDataHub — avoid a second "Fleet Control Centre" banner here */}
      {useMockData && (
        <div className="flex justify-end">
          <span className="px-2 py-0.5 text-xs font-medium bg-warning/30 text-warning rounded border border-warning/50">
            Demo data
          </span>
        </div>
      )}
      <ControlCentreCards
        onNavigate={onNavigate}
        useMockData={useMockData}
        ordersDailyEnabled={ordersDailyEnabled}
        ordersUnavailableMessage={ordersUnavailableMessage}
        telematicsTotals={telematicsTotals}
        active={active}
      />
    </div>
  );
}
